import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { EASE } from "@/lib/site";
import { WelcomeBgVideo, WelcomeVideoCard } from "@/components/WelcomeVideo";
import SmartImg from "@/lib/img";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden py-[0.5vw]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

function StarOfDavid({ className }) {
  return (
    <svg viewBox="0 0 100 100" className={className} fill="none" stroke="currentColor" strokeWidth="1">
      <polygon points="50,8 86,70 14,70" />
      <polygon points="50,92 14,30 86,30" />
    </svg>
  );
}

export default function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 60, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 60, damping: 20 });

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMouseMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
      style={{ perspective: 1200 }}
    >
      <motion.div className="absolute inset-0 -z-10" style={{ y: bgY }}>
        <SmartImg
          src="/img/firstparty/sliderhaftorah.webp"
          sizes="100vw"
          alt=""
          fetchPriority="high"
          decoding="async"
          className="w-full h-[120%] object-cover opacity-40"
        />
        <WelcomeBgVideo />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/70 to-ink" />
      </motion.div>

      <motion.div
        style={{ rotateX, rotateY, opacity: fade }}
        className="absolute right-[6%] top-[16%] md:top-[14%] w-40 md:w-72 text-gold/70 pointer-events-none"
        data-testid="hero-star"
      >
        <div className="animate-spin-slow">
          <StarOfDavid className="w-full h-auto drop-shadow-[0_0_40px_rgba(199,162,83,0.25)]" />
        </div>
      </motion.div>

      <div className="max-w-[1400px] mx-auto w-full px-6 md:px-10 pb-14 md:pb-20 pt-40">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-[10px] md:text-xs tracking-[0.35em] uppercase text-gold mb-6"
          data-testid="hero-overline"
        >
          Beth El Center for Jewish Life · West Palm Beach · Centennial 1926—2026
        </motion.p>

        <h1 className="font-syne font-extrabold uppercase leading-[0.88] tracking-tighter text-[9vw] md:text-[6.4vw]">
          <MaskedLine delay={0.35}>A Century</MaskedLine>
          <MaskedLine delay={0.5}>
            Of <span className="text-gold">Light</span>
          </MaskedLine>
          <MaskedLine delay={0.65}>&amp; Belonging</MaskedLine>
        </h1>

        <div className="flex justify-end mt-6 md:mt-8">
          <WelcomeVideoCard />
        </div>

        <div className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1, ease: EASE }}
            className="font-cormorant italic text-lg md:text-2xl text-foreground/70 max-w-md"
          >
            "L'dor v'dor — from generation to generation, a warm Jewish home in the heart of West Palm Beach."
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.15, ease: EASE }}
            className="flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-plan-visit-button"
              onClick={() => navigate("/contact")}
              className="px-8 py-4 bg-gold text-inkdeep text-xs tracking-[0.25em] uppercase font-medium hover:bg-gold-light transition-colors duration-400"
            >
              Plan Your Visit
            </button>
            <a
              data-testid="hero-livestream-button"
              href="https://www.bethelwpb.com/worship/links"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 border border-white/25 text-foreground text-xs tracking-[0.25em] uppercase hover:border-gold hover:text-gold transition-colors duration-400"
            >
              Watch Livestream
            </a>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-foreground/40"
      >
        <ArrowDown size={18} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
